import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { knowledge } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import { PageLayout, Card, TextInput, Select, TextArea, Button, ErrorMessage, SkeletonForm, ConfirmModal } from '../../components/ui'
import { knowledgeCategories } from '../../utils/constants'
import VersionHistoryModal from '../../components/VersionHistoryModal'

export default function KnowledgeEditPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const isNew = !id || id === 'new'

  // Check for prefill data from navigation state (e.g., from chat correction)
  const prefillData = location.state?.prefill
  const sourceInfo = location.state?.source

  const [formData, setFormData] = useState({
    title: prefillData?.title || '',
    content: prefillData?.content || '',
    category: prefillData?.category || 'faq',
    tags: prefillData?.tags || '',
  })
  const [error, setError] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showHistory, setShowHistory] = useState(false)

  const { data: item, isLoading: isLoadingItem } = useQuery({
    queryKey: ['knowledge', id],
    queryFn: () => knowledge.get(id),
    enabled: !isNew,
  })

  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || '',
        content: item.content || '',
        category: item.category || 'faq',
        tags: item.tags || '',
      })
    }
  }, [item])

  const saveMutation = useMutation({
    mutationFn: (data) => isNew ? knowledge.create(data) : knowledge.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge'] })
      if (!isNew) queryClient.invalidateQueries({ queryKey: ['knowledge', id] })
      navigate('/knowledge')
    },
    onError: (err) => {
      setError(err.message || 'Failed to save')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => knowledge.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge'] })
      navigate('/knowledge')
    },
    onError: (err) => {
      setError(err.message || 'Failed to delete')
    },
  })

  function handleSubmit(e) {
    e.preventDefault()
    setError('')
    saveMutation.mutate(formData)
  }

  function handleChange(field) {
    return (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  function handleDelete() {
    setShowDeleteConfirm(false)
    deleteMutation.mutate()
  }

  // Build category options from constants (key = API value, value = display label)
  const categoryOptions = Object.entries(knowledgeCategories).map(([key, label]) => ({
    value: key,
    label: label,
  }))

  if (!isNew && isLoadingItem) {
    return (
      <PageLayout>
        <PageHeader title="Edit Knowledge" showBack backTo="/knowledge" />
        <div className="p-4">
          <Card>
            <SkeletonForm fields={4} />
          </Card>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <PageHeader
        title={isNew ? 'New Entry' : 'Edit Entry'}
        showBack
        backTo="/knowledge"
      />

      <form onSubmit={handleSubmit} className="px-4 py-4 space-y-4">
        <ErrorMessage error={error} />

        {sourceInfo?.type === 'chat_correction' && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">Creating from AI response</span>
            </div>
            <p className="mt-1 text-amber-700">
              {sourceInfo.clientName ? `From ${sourceInfo.clientName}'s conversation. ` : ''}
              Edit the template below to document the correct approach.
            </p>
          </div>
        )}

        <Card>
          <div className="space-y-4">
            <TextInput
              label="Title"
              value={formData.title}
              onChange={handleChange('title')}
              required
              placeholder="e.g., Proper Squat Form"
            />

            <Select
              label="Category"
              value={formData.category}
              onChange={handleChange('category')}
              options={categoryOptions}
            />

            <TextArea
              label="Content"
              value={formData.content}
              onChange={handleChange('content')}
              rows={8}
              required
              placeholder="Enter your knowledge content here..."
            />

            <TextInput
              label="Tags"
              value={formData.tags}
              onChange={handleChange('tags')}
              placeholder="e.g., strength, form, technique (comma-separated)"
              hint="Separate tags with commas"
            />
          </div>
        </Card>

        <Button
          type="submit"
          fullWidth
          loading={saveMutation.isPending}
        >
          {isNew ? 'Create Entry' : 'Save Changes'}
        </Button>

        {!isNew && (
          <Button
            type="button"
            variant="secondary"
            fullWidth
            onClick={() => setShowHistory(true)}
          >
            Version History
          </Button>
        )}

        {!isNew && (
          <Button
            type="button"
            variant="danger"
            fullWidth
            loading={deleteMutation.isPending}
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete Entry
          </Button>
        )}
      </form>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Entry"
        message="Are you sure you want to delete this knowledge entry? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />

      {!isNew && (
        <VersionHistoryModal
          isOpen={showHistory}
          onClose={() => {
            setShowHistory(false)
            queryClient.invalidateQueries({ queryKey: ['knowledge', id] })
          }}
          entityId={parseInt(id)}
          entityType="knowledge article"
          api={knowledge}
          queryKeyPrefix="knowledge"
          renderContent={(v) => (
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-indices-muted">Title: </span>
                <span className="text-indices-bright">{v.title}</span>
              </div>
              <div>
                <span className="text-indices-muted">Category: </span>
                <span className="text-indices-bright">{v.category}</span>
              </div>
              {v.tags && (
                <div>
                  <span className="text-indices-muted">Tags: </span>
                  <span className="text-indices-bright">{v.tags}</span>
                </div>
              )}
              <div>
                <span className="text-indices-muted">Active: </span>
                <span className={v.isActive ? 'text-green-400' : 'text-red-400'}>
                  {v.isActive ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="mt-2">
                <span className="text-indices-muted text-xs block mb-1">Content:</span>
                <pre className="text-lume-teal bg-dial-black rounded-lg p-3 text-xs whitespace-pre-wrap max-h-48 overflow-y-auto border border-dial-border">
                  {v.content}
                </pre>
              </div>
            </div>
          )}
        />
      )}
    </PageLayout>
  )
}
